/**
 * Render helpers for CardBody component
 * Contains functions to render repetitive JSX patterns
 */

import React from "react";
import ProjectNameDisplay from "../trackerComponents/ProjectNameDisplay.jsx";
import BillableIcon from "../trackerComponents/BillableIcon.jsx";
import ActionsDropdown from "../trackerComponents/ActionsDropdown.jsx";
import StatusDropdown from "../trackerComponents/StatusDropdown.jsx";
import { MESSAGES, PLACEHOLDERS, DEFAULT_VALUES, STATUS_OPTIONS } from "./cardBodyConstants.js";
import { isEntryUnlocked } from "../utils/cardBodyDateHelpers.js";
import userTypeConsts from "../../../../../constants/userTypeConsts.js";
import TopArrow from "../../../../../images/icons/top-small-arrow.svg";
import DownArrow from "../../../../../images/icons/bottom-small-arrow.svg";

/**
 * Renders project name display for edit mode
 */
export const renderProjectNameDisplay = ({
  projectNameEdit,
  sprintNameEdit,
  clientEdit,
  projectIdEdit,
  className = "",
  style = {},
}) => {
  return (
    <ProjectNameDisplay
      projectName={projectNameEdit}
      sprintName={sprintNameEdit}
      clientName={clientEdit}
      projectId={projectIdEdit}
      className={className}
      style={style}
    />
  );
};

/**
 * Renders project name display for view mode
 */
export const renderProjectNameView = ({
  projectName,
  sprintName,
  clientName,
  className = "",
  style = {},
}) => {
  return (
    <ProjectNameDisplay
      projectName={projectName}
      sprintName={sprintName}
      clientName={clientName}
      className={className}
      style={style}
      showHiddenInput={false}
    />
  );
};

/**
 * Renders billable icon
 */
export const renderBillableIcon = ({ 
  billable, 
  className = "",
  group,
  row,
  groupIndex,
  toggleGroup,
  getLockHoursHandler,
  globalConfig,
  setClickId,
  initializeEditStateFromGroup,
  effectiveSetEditedId,
  editedId,
  setProjectEditDropdownOpen,
  setTagEditDropdownOpen,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintNameEdit,
  setSprintIdEdit,
  setDateEdit,
  setTagIdEdit,
  setTagEdit,
  setTagQuery,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeTemp,
  setEndTimeTemp,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setUFormatedTime,
  setUDuration,
  setUStartTimeError,
  setUEndTimeError,
  setUFormatedTimeError,
  handleOpenPopup,
}) => {
  const handleClick = () => {
    const currentItem = row || group;
    if (!currentItem) {
      return;
    }
    
    // Check if we're already in edit mode for this item
    const isInEditMode = editedId === currentItem._id;
    
    // If already in edit mode, do nothing (prevent reinitializing state)
    if (isInEditMode) {
      return;
    }
    
    const isUnlocked = isEntryUnlocked(currentItem.endTime, globalConfig, getLockHoursHandler);
    
    // Handle row clicks (individual entries within merged groups)
    if (row) {
      if (isUnlocked) {
        if (initializeEditStateFromGroup) {
          initializeEditStateFromGroup({
            group: row,
            effectiveSetEditedId,
            setProjectEditDropdownOpen,
            setTagEditDropdownOpen,
            setTaskNameEdit,
            setProjectIdEdit,
            setProjectNameEdit,
            setSprintNameEdit,
            setSprintIdEdit,
            setDateEdit,
            setTagIdEdit,
            setTagEdit,
            setTagQuery,
            setStartTimeEdit,
            setEndTimeEdit,
            setStartTimeTemp,
            setEndTimeTemp,
            setStartTimeEdit24,
            setEndTimeEdit24,
            setClientEdit,
            setClientIdEdit,
            setBillableEdit,
            setUFormatedTime,
            setUDuration,
            setUStartTimeError,
            setUEndTimeError,
            setUFormatedTimeError,
            globalConfig,
          });
        }
      } else {
        if (handleOpenPopup) {
          handleOpenPopup();
        }
        if (setClickId) {
          setClickId(row._id);
        }
      }
      return;
    }
    
    // Handle group clicks
    if (group) {
      if (isUnlocked) {
        // For merged groups (entries.length > 1), toggle to expand/collapse
        // For single entry groups (entries.length === 1), enter edit mode
        if (group.entries.length === 1) {
          if (initializeEditStateFromGroup) {
            initializeEditStateFromGroup({
              group,
              effectiveSetEditedId,
              setProjectEditDropdownOpen,
              setTagEditDropdownOpen,
              setTaskNameEdit,
              setProjectIdEdit,
              setProjectNameEdit,
              setSprintNameEdit,
              setSprintIdEdit,
              setDateEdit,
              setTagIdEdit,
              setTagEdit,
              setTagQuery,
              setStartTimeEdit,
              setEndTimeEdit,
              setStartTimeTemp,
              setEndTimeTemp,
              setStartTimeEdit24,
              setEndTimeEdit24,
              setClientEdit,
              setClientIdEdit,
              setBillableEdit,
              setUFormatedTime,
              setUDuration,
              setUStartTimeError,
              setUEndTimeError,
              setUFormatedTimeError,
              globalConfig,
              useMomentFormat: false,
            });
          }
        } else {
          // Toggle group for merged entries (consistent with project-sprint, task, time, tag sections)
          if (toggleGroup && groupIndex !== undefined) {
            toggleGroup(groupIndex);
          }
        }
      } else {
        // Entry is locked
        if (group.entries.length === 1) {
          if (setClickId) {
            setClickId(group._id);
          }
          if (handleOpenPopup) {
            handleOpenPopup();
          }
        } else {
          // For merged groups, still toggle even if locked (consistent with other sections)
          if (toggleGroup && groupIndex !== undefined) {
            toggleGroup(groupIndex);
          }
          if (setClickId) {
            setClickId(null);
          }
        }
      }
    }
  };
  
  // Only add onClick handler if we have group or row data
  const shouldHandleClick = (group && group.entries) || row;
  
  return (
    <BillableIcon 
      billable={billable} 
      className={className}
      onClick={shouldHandleClick ? handleClick : undefined}
    />
  );
};

/**
 * Renders actions dropdown menu
 */
export const renderActionsDropdown = ({
  isOpen,
  activeId,
  itemId,
  duplicateLoading,
  isDeleteLoading,
  deleteId,
  onDuplicate,
  onDelete,
  onMouseDown = (e) => e.stopPropagation(),
  anchorEl,
}) => {
  return (
    <ActionsDropdown
      isOpen={isOpen}
      activeId={activeId}
      itemId={itemId}
      duplicateLoading={duplicateLoading}
      isDeleteLoading={isDeleteLoading}
      deleteId={deleteId}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      onMouseDown={onMouseDown}
      anchorEl={anchorEl}
    />
  );
};

/**
 * Renders status dropdown for project filter
 */
export const renderProjectStatusDropdown = ({
  user,
  label = MESSAGES.FILTER_BY_STATUS,
  statusFilter,
  setStatusFilter,
  isDropdownOpen,
  setIsDropdownOpen,
  dropdownRef,
}) => {
  if (user.userType !== userTypeConsts.SUPER_ADMIN && user.userType !== userTypeConsts.ADMIN) {
    return null;
  }

  return (
    <StatusDropdown
      label={label}
      statusFilter={statusFilter || DEFAULT_VALUES.PROJECT_STATUS_FILTER}
      setStatusFilter={setStatusFilter}
      isDropdownOpen={isDropdownOpen}
      setIsDropdownOpen={setIsDropdownOpen}
      dropdownRef={dropdownRef}
    />
  );
};

/**
 * Renders project dropdown search input
 */
export const renderProjectSearchInput = ({
  filter,
  setFilter,
  projectStatusFilter,
  filteredClientData,
  setSelectedClients,
  onMouseDown = (e) => e.stopPropagation(),
}) => {
  return (
    <div className="top-search-tracker-for">
      <input
        type="text"
        className="search-bar"
        placeholder={PLACEHOLDERS.FIND_PROJECT_OR_CLIENT}
        value={filter}
        onChange={(e) => {
          const filterValue = e.target.value;
          setFilter(filterValue);
          if (filterValue === "") {
            setSelectedClients(new Set());
          } else {
            const matchingClients = filteredClientData.filter((client) =>
              client.projects.some(
                (project) =>
                  project.project_name.toLowerCase().includes(filterValue.toLowerCase()) &&
                  (projectStatusFilter === STATUS_OPTIONS.ALL
                    ? true
                    : projectStatusFilter === STATUS_OPTIONS.ACTIVE
                      ? project.is_active === true
                      : project.is_active === false)
              )
            );
            setSelectedClients(new Set(matchingClients));
          }
        }}
        autoComplete="off"
        onMouseDown={onMouseDown}
      />
    </div>
  );
};

/**
 * Renders client list in project dropdown
 */
export const renderClientList = ({
  filteredClientData,
  filter,
  projectStatusFilter,
  selectedClients,
  setSelectedClients,
  selectedProject,
  setSelectedProject,
  setSprintIdEdit,
  setSprintNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setProjectEditDropdownOpen,
  user,
  setClickId,
  group,
  createProjectSelectHandler,
  createSprintSelectHandler,
  onMouseDown = (e) => e.stopPropagation(),
}) => {
  const isAdmin = user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN;
  const filterClass = isAdmin ? "" : "filter-active-no";

  return (
    <ul
      className={`tracker-who-list client-list client-list-filter ${filterClass}`}
      onMouseDown={onMouseDown}
    >
      {filteredClientData.map((client) => {
        const matchingProjects = client.projects.filter(
          (project) =>
            project.project_name.toLowerCase().includes(filter.toLowerCase()) &&
            (projectStatusFilter === STATUS_OPTIONS.ALL
              ? true
              : projectStatusFilter === STATUS_OPTIONS.ACTIVE
                ? project.is_active === true
                : project.is_active === false)
        );

        return (
          <div key={client._id} className="main-outer-item-dd" onMouseDown={onMouseDown}>
            <div
              className={`client-name1 ${selectedClients.has(client) ? "selected" : ""}`}
              onClick={() => {
                if (setClickId && group) {
                  setClickId(group.entries.length === 1 ? group._id : null);
                }
                if (matchingProjects.length > 0) {
                  const newSelectedClients = new Set(selectedClients);
                  if (selectedClients.has(client)) {
                    newSelectedClients.delete(client);
                  } else {
                    newSelectedClients.add(client);
                  }
                  setSelectedClients(newSelectedClients);
                }
              }}
              onMouseDown={onMouseDown}
            >
              <span className="main-project">{client.client_name} </span>
              {matchingProjects.length > 0 && (
                <span className={`arrow ${selectedClients.has(client) ? "open" : ""}`} onMouseDown={onMouseDown}>
                  <span className="main-project-title">
                    {matchingProjects.length} {MESSAGES.PROJECTS}
                  </span>
                  {selectedClients.has(client) ? (
                    <img src={TopArrow} alt="Open" />
                  ) : (
                    <img src={DownArrow} alt="Close" />
                  )}
                </span>
              )}
            </div>
            {selectedClients.has(client) && (
              <div className="project-name1-outer">
                {matchingProjects.length === 0 ? (
                  <div>{MESSAGES.NO_PROJECTS_UNDER_CLIENT}</div>
                ) : (
                  matchingProjects
                    .sort((a, b) => a.project_name.localeCompare(b.project_name))
                    .map((project) => (
                      <div key={project._id}>
                        <div className={`project-name1 ${selectedProject === project ? "selected" : ""}`}>
                          <div
                            onClick={
                              createProjectSelectHandler
                                ? createProjectSelectHandler({
                                    project,
                                    client,
                                    setSprintIdEdit,
                                    setSprintNameEdit,
                                    setProjectIdEdit,
                                    setProjectNameEdit,
                                    setClientEdit,
                                    setClientIdEdit,
                                    setBillableEdit,
                                    setProjectEditDropdownOpen,
                                  })
                                : () => {
                                    setSprintIdEdit("");
                                    setSprintNameEdit("");
                                    setProjectIdEdit(project._id);
                                    setProjectNameEdit(project.project_name);
                                    setClientEdit(client.client_name);
                                    setClientIdEdit(client._id);
                                    setBillableEdit(project.is_billable);
                                    setProjectEditDropdownOpen(false);
                                  }
                            }
                            onMouseDown={onMouseDown}
                          >
                            <span className="project-dd-title">{project.project_name} </span>
                          </div>
                          <div>
                            {project.sprints.length > 0 && (
                              <span
                                className={`arrow ${selectedProject === project ? "open" : ""}`}
                                onClick={() => setSelectedProject(selectedProject === project ? null : project)}
                                onMouseDown={onMouseDown}
                              >
                                <span className="main-project-title">
                                  {project.sprints.length} Sprints
                                </span>
                                {selectedProject === project ? (
                                  <img src={TopArrow} alt="Open" />
                                ) : (
                                  <img src={DownArrow} alt="Close" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        {selectedProject === project && (
                          <div>
                            {project.sprints.length === 0 ? (
                              <div>{MESSAGES.NO_SPRINTS_UNDER_PROJECT}</div>
                            ) : (
                              <ul className="sprint-list" onMouseDown={onMouseDown}>
                                {project.sprints
                                  .sort((a, b) => a.sprint_name.localeCompare(b.sprint_name))
                                  .map((sprint) => (
                                    <li
                                      key={sprint._id}
                                      onClick={
                                        createSprintSelectHandler
                                          ? createSprintSelectHandler({
                                              sprint,
                                              project,
                                              client,
                                              setSprintIdEdit,
                                              setSprintNameEdit,
                                              setProjectIdEdit,
                                              setProjectNameEdit,
                                              setClientEdit,
                                              setClientIdEdit,
                                              setBillableEdit,
                                              setProjectEditDropdownOpen,
                                            })
                                          : () => {
                                              setSprintIdEdit(sprint._id);
                                              setSprintNameEdit(sprint.sprint_name);
                                              setProjectIdEdit(project._id);
                                              setProjectNameEdit(project.project_name);
                                              setClientEdit(client.client_name);
                                              setClientIdEdit(client._id);
                                              setBillableEdit(project.is_billable);
                                              setProjectEditDropdownOpen(false);
                                            }
                                      }
                                      onMouseDown={onMouseDown}
                                    >
                                      {sprint.sprint_name}
                                    </li>
                                  ))}
                              </ul>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </ul>
  );
};
